// telephonyVerlauf.ts — reine Helfer für den Verlauf der Telefon-Seite:
// serverseitige Suche, seitenweises Nachladen (Infinite Scroll) und den
// Verpasst-Tab. Bewusst ohne React/DOM/Fetch, damit Vitest sie direkt prüft.

import type { CallLogDto, ListCallsQuery } from "../../services/telephonyService";
import { isOpenMissed } from "./telephonyUtils";

/** Zeilen je nachgeladener Seite. */
export const VERLAUF_SEITE = 50;
/** Obergrenze des Backends für `limit` (ListCallLogQueryDto). */
export const VERLAUF_MAX_LIMIT = 500;
/** Ab so vielen Zeichen geht eine Suche an den Server. */
export const MIN_SUCHLAENGE = 2;
/** Ab so vielen Ziffern gilt ein Begriff als Nummer (wie im Backend). */
export const MIN_NUMMER_ZIFFERN = 3;

export type VerlaufTab = "verlauf" | "verpasst";

/**
 * Der Suchbegriff, der an den Server geht — oder "" für „keine Suche".
 * Ein einzelnes Zeichen sucht nicht: Es träfe fast alles und kostet eine
 * Abfrage über die ganze Historie.
 */
export function effektiveSuche(raw: string): string {
    const t = (raw ?? "").trim();
    return t.length >= MIN_SUCHLAENGE ? t : "";
}

/**
 * Sieht der Begriff wie eine Telefonnummer aus? Nur Ziffern und übliche Trenner
 * (Leerzeichen, +, -, /, Klammern, Punkt) und mindestens drei Ziffern. Dieselbe
 * Regel wie `siehtWieNummerAus` im Backend (`call-search.ts`), damit „Anrufen"
 * genau dann angeboten wird, wenn der Server auch nach der Nummer sucht.
 */
export function siehtWieNummerAus(raw: string): boolean {
    const t = (raw ?? "").trim();
    if (!/^[\d\s+\-/().]+$/.test(t)) return false;
    return t.replace(/\D/g, "").length >= MIN_NUMMER_ZIFFERN;
}

/**
 * Die Abfrage für eine Seite des Verlaufs. Zeitraum, Bereich und Suche wirken
 * serverseitig; der Verpasst-Tab schickt `missedOnly`, damit er genauso
 * seitenweise nachladen kann wie der Verlauf.
 */
export function verlaufQuery(p: {
    tab: VerlaufTab;
    range: { from?: string; to?: string };
    scope: "all" | "own" | "team";
    suche: string;
    offset: number;
    limit: number;
}): ListCallsQuery {
    const q: ListCallsQuery = {
        ...p.range,
        scope: p.scope,
        limit: Math.max(1, Math.min(p.limit, VERLAUF_MAX_LIMIT)),
        offset: Math.max(0, p.offset),
    };
    const suche = effektiveSuche(p.suche);
    if (suche) q.search = suche;
    if (p.tab === "verpasst") q.missedOnly = true;
    return q;
}

/**
 * Ab welcher Position die nächste Seite beginnt. Im Verpasst-Tab zählen nur
 * Anrufe, die noch offen verpasst sind: Ein gerade abgehakter Anruf steht
 * weiter in der geladenen Liste (das Detail bleibt offen), passt aber nicht
 * mehr zum Serverfilter — ihn mitzuzählen, übersprünge einen Anruf.
 */
export function ladeOffset(calls: readonly CallLogDto[], tab: VerlaufTab): number {
    return tab === "verpasst" ? calls.filter(isOpenMissed).length : calls.length;
}

/** Was der Tab zeigt: im Verpasst-Tab nur die noch offenen. */
export function sichtbareAnrufe(calls: readonly CallLogDto[], tab: VerlaufTab): CallLogDto[] {
    return tab === "verpasst" ? calls.filter(isOpenMissed) : [...calls];
}

/**
 * Nächste Seite anhängen. Doppelte ids fallen raus — kommt zwischendurch ein
 * neuer Anruf hinzu, verschiebt sich das Fenster um eine Zeile, und die
 * nächste Seite beginnt mit einer, die schon da ist.
 */
export function seiteAnhaengen(prev: readonly CallLogDto[], next: readonly CallLogDto[]): CallLogDto[] {
    const seen = new Set(prev.map((c) => c.id));
    return [...prev, ...next.filter((c) => !seen.has(c.id))];
}

/**
 * Wie viele Zeilen ein Neuladen „an Ort und Stelle" holt: mindestens eine
 * Seite, sonst so viele, wie schon geladen sind — damit die Liste nicht
 * zusammenschrumpft und die Scrollposition stehen bleibt.
 */
export function neuLadenLimit(geladen: number): number {
    return Math.min(Math.max(geladen, VERLAUF_SEITE), VERLAUF_MAX_LIMIT);
}

export type Bereich = "all" | "own" | "team";

/**
 * Welche Sichtbarkeitsbereiche bietet die Anrufliste an?
 *  - „Alle" (ganzer Mandant) nur dem Admin.
 *  - „Team" nur mit Team — und nicht der Geschäftsführung (T-37, PO 30.09.2026:
 *    sie sieht in der Anrufliste nur ihre eigenen Anrufe; das Backend klemmt
 *    ohnehin auf „Persönlich").
 *  - „Persönlich" immer.
 * Reihenfolge = Reihenfolge der Knöpfe.
 */
export function bereichsOptionen(o: {
    isAdmin: boolean;
    isGfgl: boolean;
    hasTeam: boolean;
}): { value: Bereich; label: string }[] {
    const opts: { value: Bereich; label: string }[] = [];
    if (o.isAdmin) opts.push({ value: "all", label: "Alle" });
    if (o.hasTeam && !o.isGfgl) opts.push({ value: "team", label: "Team" });
    opts.push({ value: "own", label: "Persönlich" });
    return opts;
}
